import { humanDuration } from '../lib/duration';
import { tierColor } from '../lib/palette';
import { formatBytes, formatCount, type StorageEstimate } from '../lib/storage';

const NODE_W = 196;
const NODE_H = 118;
const GAP = 46;
const PAD = 8;

interface Props {
  estimate: StorageEstimate;
}

/**
 * Raw → tier → tier cascade, with each hop labelled by resolution, retention and
 * resident volume, and a bar showing volume relative to the largest bucket. The
 * whole thing is a single scalable SVG so it stays crisp and costs no bundle weight.
 */
export default function CascadeDiagram({ estimate }: Props) {
  const levels = estimate.levels;
  const width = PAD * 2 + levels.length * NODE_W + (levels.length - 1) * GAP;
  const height = NODE_H + PAD * 2 + 26;
  const max = Math.max(...levels.map((l) => l.steadyStateBytes), 1);

  const summary = levels
    .map(
      (l) =>
        `${l.bucket} at ${humanDuration(l.resolutionSeconds)} resolution keeps ` +
        `${humanDuration(l.retentionDays * 86400)} and holds ${formatBytes(l.steadyStateBytes)}`,
    )
    .join('; ');

  return (
    <div className="table-scroll">
      <svg
        className="chart"
        viewBox={`0 0 ${width} ${height}`}
        style={{ minWidth: width / 1.6 }}
        role="img"
        aria-label={`Downsampling cascade: ${summary}.`}
      >
        {levels.map((level, i) => {
          const x = PAD + i * (NODE_W + GAP);
          const color = i === 0 ? 'var(--border-strong)' : tierColor(i - 1);
          const barWidth = Math.max(3, (level.steadyStateBytes / max) * (NODE_W - 24));
          return (
            <g key={level.bucket + i}>
              {i > 0 ? (
                <g>
                  <line
                    x1={x - GAP + 6}
                    y1={PAD + NODE_H / 2}
                    x2={x - 9}
                    y2={PAD + NODE_H / 2}
                    stroke="var(--border-strong)"
                    strokeWidth={2}
                  />
                  <path
                    d={`M ${x - 9} ${PAD + NODE_H / 2} l -9 -5 v 10 z`}
                    fill="var(--border-strong)"
                  />
                </g>
              ) : null}
              <rect
                x={x}
                y={PAD}
                width={NODE_W}
                height={NODE_H}
                rx={9}
                fill="var(--surface-2)"
                stroke={color}
                strokeWidth={i === 0 ? 1.5 : 2}
              />
              <text
                x={x + 12}
                y={PAD + 24}
                fill="var(--text)"
                fontSize={13.5}
                fontWeight={650}
                fontFamily="ui-monospace, Menlo, Consolas, monospace"
              >
                {level.bucket}
              </text>
              <text x={x + 12} y={PAD + 44} fill="var(--text-dim)" fontSize={12}>
                {humanDuration(level.resolutionSeconds)} · keep{' '}
                {level.retentionClamped ? 'forever' : humanDuration(level.retentionDays * 86400)}
              </text>
              <text x={x + 12} y={PAD + 62} fill="var(--text-dim)" fontSize={12}>
                {formatCount(level.seriesCount)} series
              </text>
              <text x={x + 12} y={PAD + 86} fill="var(--text)" fontSize={16} fontWeight={650}>
                {formatBytes(level.steadyStateBytes)}
              </text>
              <rect
                x={x + 12}
                y={PAD + 96}
                width={barWidth}
                height={7}
                rx={3.5}
                fill={color}
                opacity={0.85}
              />
            </g>
          );
        })}
        <text x={PAD} y={height - 6} fill="var(--text-dim)" fontSize={11.5}>
          Bar length is resident volume relative to the largest bucket.
        </text>
      </svg>
    </div>
  );
}
